import type {
  IProject,
  IProjectJoinInfo,
  Project as APIProject,
  IUserApp,
} from 'oscar-typedef'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import type { Maybe } from '@/types'
import { getExecuteOnce } from '@/utils'

const useProjectAPIStore = defineStore('projectAPI', () => {
  const projectList = ref<Maybe<IProject[]>>()
  const projectPopularList = ref<Maybe<IProject[]>>()
  const projectNewList = ref<Maybe<IProject[]>>()
  const projectJoinInfoList = ref<Maybe<IProjectJoinInfo[]>>()
  const appList = ref<Maybe<IUserApp[]>>()

  const createProject = async (
    params: APIProject.CreateProject.Request.Body,
  ) => {
    if (!params) return
    const res =
      await WepinBackendAPI.getInstance().project.createProject(params)
    if (!res) return
    if (!projectList.value) projectList.value = []
    projectList.value = projectList.value.concat(res)
    return res
  }

  const modifyProject = async (
    projectId: IProject['id'],
    params: APIProject.ModifyProject.Request.Body,
  ) => {
    if (!projectId) return
    const res = await WepinBackendAPI.getInstance().project.modifyProject(
      { projectId },
      params,
    )
    if (!res) return
    projectList.value = projectList.value?.map((project) =>
      project.id === res.id ? res : project,
    )
    return res
  }

  const deleteProject = async (projectId: IProject['id']) => {
    if (!projectId) return
    const res = await WepinBackendAPI.getInstance().project.deleteProject({
      projectId,
    })

    if (!res) return
    if (res.result) {
      projectList.value = projectList.value?.filter(
        (project) => project.id !== projectId,
      )
    }
    return res
  }

  const registerProject = async (
    projectId: IProject['id'],
    params: APIProject.RegisterProject.Request.Body,
  ) => {
    if (!projectId || !params) return
    const res = await WepinBackendAPI.getInstance().project.registerProject(
      { projectId },
      params,
    )
    if (!res) return
    return res.id
  }

  const deregisterProject = async (projectId: IProject['id']) => {
    if (!projectId) return
    const res = await WepinBackendAPI.getInstance().project.deregisterProject({
      projectId,
    })
    if (!res) return
    return res
  }

  // const initProject = async (projectId: IProject['id']) => {
  //   if (!projectId) return
  //   const res = await WepinBackendAPI.getInstance().project.initProject({
  //     projectId,
  //   })
  //   if (!res) return
  //   return res
  // }

  const fetchProject = getExecuteOnce(
    async (
      projectId: IProject['id'],
      params?: APIProject.GetProject.Request.Queries,
      force: boolean | undefined = false,
    ) => {
      if (!projectId) return
      if (force || !projectList.value) {
        const res = await WepinBackendAPI.getInstance().project.getProject(
          { projectId },
          params,
        )
        if (!res) return
        projectList.value = projectList.value?.map((project) =>
          project.id === projectId ? res : project,
        )
        return res
      }
      return projectList.value.find((project) => project.id === projectId)
    },
  )

  const fetchProjectList = getExecuteOnce(
    async (
      params: APIProject.GetProjectList.Request.Queries,
      force: boolean | undefined = false,
    ) => {
      if (!params) return
      if (force || !projectList.value) {
        const res =
          await WepinBackendAPI.getInstance().project.getProjectList(params)
        if (!res) return
        if (!projectList.value) projectList.value = []
        projectList.value = projectList.value.concat(res.projects)
      }
      return projectList.value
    },
  )

  const fetchPopularProjectList = getExecuteOnce(
    async (
      params: APIProject.GetPopularProjectList.Request.Queries,
      force: boolean | undefined = false,
    ) => {
      if (!params) return
      if (force || !projectPopularList.value) {
        const res =
          await WepinBackendAPI.getInstance().project.getPopularProjectList(
            params,
          )
        if (!res) return
        if (!projectPopularList.value) projectPopularList.value = []
        projectPopularList.value = projectPopularList.value.concat(res.projects)
      }
      return projectPopularList.value
    },
  )

  const fetchNewProjectList = getExecuteOnce(
    async (
      params: APIProject.GetNewProjectList.Request.Queries,
      force: boolean | undefined = false,
    ) => {
      if (!params) return
      if (force || !projectNewList.value) {
        const res =
          await WepinBackendAPI.getInstance().project.getNewProjectList(params)
        if (!res) return
        if (!projectNewList.value) projectNewList.value = []
        projectNewList.value = projectNewList.value.concat(res.projects)
      }
      return projectNewList.value
    },
  )

  const fetchProjectTokenList = getExecuteOnce(
    async (projectId: IProject['id']) => {
      if (!projectId) return
      const res =
        await WepinBackendAPI.getInstance().project.getProjectTokenList({
          projectId,
        })
      if (!res) return
      return res.tokens
    },
  )

  const fetchProjectNFTList = getExecuteOnce(
    async (projectId: IProject['id']) => {
      if (!projectId) return
      const res = await WepinBackendAPI.getInstance().project.getProjectNFTList(
        {
          projectId,
        },
      )
      if (!res) return
      return res.nfts
    },
  )

  const setLikeProject = async (userProjectJoinId: number) => {
    if (!userProjectJoinId) return
    const res = await WepinBackendAPI.getInstance().project.setLikeProject({
      userProjectJoinId,
    })
    if (!res) return
    return res
  }

  const unsetLikeProject = async (userProjectJoinId: number) => {
    if (!userProjectJoinId) return
    const res = await WepinBackendAPI.getInstance().project.unsetLikeProject({
      userProjectJoinId,
    })
    if (!res) return
    return res
  }

  const updateProjectJoinUserInfo = async (
    userProjectJoinId: number,
    params: APIProject.UpdateProjectJoinUserInfo.Request.Body,
  ) => {
    if (!userProjectJoinId || !params) return
    const res =
      await WepinBackendAPI.getInstance().project.updateProjectJoinUserInfo(
        { userProjectJoinId },
        params,
      )
    if (!res) return
    return res
  }

  const dropOutUser = async (userProjectJoinId: number) => {
    if (!userProjectJoinId) return
    const res = await WepinBackendAPI.getInstance().project.dropOutUser({
      userProjectJoinId,
    })
    if (!res) return
    return res
  }

  const fetchUserProjectList = getExecuteOnce(
    async (force: boolean | undefined = false) => {
      if (force || !projectJoinInfoList.value) {
        const res =
          await WepinBackendAPI.getInstance().project.getUserProjectList()
        if (!res) return
        if (!projectJoinInfoList.value) projectJoinInfoList.value = []
        projectJoinInfoList.value = projectJoinInfoList.value.concat(
          res.projectJoinInfo,
        )
      }
      return projectJoinInfoList.value
    },
  )

  const fecthUserAppDetail = async (force: boolean | undefined = false) => {
    if (force || appList.value) {
      const res = await WepinBackendAPI.getInstance().project.getUserAppDetail()
      if (!res) return

      appList.value = res
    }
    return appList.value
  }

  const getUserAppDetailById = async (
    appId: APIProject.GetUserAppDetailById.Request.Params,
  ) => {
    const res =
      await WepinBackendAPI.getInstance().project.getUserAppDetailByID(appId)
    return res
  }

  const reset = () => {
    projectList.value = []
    projectPopularList.value = []
    projectNewList.value = []
    projectJoinInfoList.value = []
    appList.value = []
  }

  return {
    projectList,
    projectPopularList,
    projectNewList,
    projectJoinInfoList,
    appList,
    createProject,
    modifyProject,
    deleteProject,
    registerProject,
    deregisterProject,
    // initProject,
    fetchProject,
    fetchProjectList,
    fetchPopularProjectList,
    fetchNewProjectList,
    fetchProjectTokenList,
    fetchProjectNFTList,
    setLikeProject,
    unsetLikeProject,
    updateProjectJoinUserInfo,
    dropOutUser,
    fetchUserProjectList,
    fecthUserAppDetail,
    getUserAppDetailById,
    reset,
  }
})

export { useProjectAPIStore }
